import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { apiSend } from "../../api";
import { date, money } from "../../format";
import { Modal } from "./Modal";
import { useTicketContext } from "./useTicket";
import { confirmBox } from "../../confirm";
import { cardBrand, cardCodeProblem, cardNumberProblem, codeLength, formatCardNumber } from "../../cardNumber";

/** Old-system cards have no type or last four until cutover: "Card on file" instead of "ending ????". */
export const cardLabel = (c: { cardType: string | null; last4: string | null }) =>
  c.last4 ? `${c.cardType ?? "Card"} ending ${c.last4}` : `${c.cardType ? `${c.cardType} card` : "Card"} on file`;

/** Opens Authorize.Net's own card page in a new tab (card numbers never touch the portal). */
async function openHostedCardPage(open: () => Promise<{ url: string; token: string }>) {
  const tab = window.open("", "_blank");
  try {
    const { url, token } = await open();
    if (!tab) throw new Error("The browser blocked the new tab. Allow pop-ups for the portal.");
    const doc = tab.document;
    doc.title = "Opening Authorize.Net…";
    const form = doc.createElement("form");
    form.method = "POST";
    form.action = url;
    const input = doc.createElement("input");
    input.type = "hidden";
    input.name = "token";
    input.value = token;
    form.appendChild(input);
    doc.body.appendChild(form);
    form.submit();
  } catch (e) {
    tab?.close();
    throw e;
  }
}

type AcceptConfig = { apiLoginId: string; clientKey: string; script: string };
type AcceptResponse = { messages: { resultCode: "Ok" | "Error"; message: { code: string; text: string }[] }; opaqueData?: { dataDescriptor: string; dataValue: string } };
declare global { interface Window { Accept?: { dispatchData(d: unknown, cb: (r: AcceptResponse) => void): void } } }

/** Loads Authorize.Net's Accept.js once (it sends the card number from the browser straight to Authorize.Net). */
function loadAccept(src: string): Promise<void> {
  if (window.Accept) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const el = document.createElement("script");
    el.src = src;
    el.charset = "utf-8";
    el.onload = () => resolve();
    el.onerror = () => reject(new Error("Could not load Authorize.Net's card form. Check the connection and try again."));
    document.head.appendChild(el);
  });
}

function tokenize(cfg: AcceptConfig, card: { cardNumber: string; month: string; year: string; cardCode: string }): Promise<{ dataDescriptor: string; dataValue: string }> {
  return new Promise((resolve, reject) => {
    window.Accept!.dispatchData({ authData: { clientKey: cfg.clientKey, apiLoginID: cfg.apiLoginId }, cardData: card }, (r) => {
      if (r.messages.resultCode === "Ok" && r.opaqueData) resolve(r.opaqueData);
      else reject(new Error(r.messages.message.map((m) => m.text).join(" ") || "Authorize.Net did not accept the card."));
    });
  });
}

const MONTHS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
const YEARS = Array.from({ length: 12 }, (_, i) => String(new Date().getFullYear() + i));

/**
 * The card form, in the portal like the old Manage Payment Profile window (owner, 2026-11-03). The card number
 * and code go from this browser to Authorize.Net only (Accept.js); the portal's server gets a one-time token.
 */
function CardForm({ cfg, onDone, onCancel }: { cfg: AcceptConfig; onDone: () => void; onCancel?: () => void }) {
  const { detail, save, saving } = useTicketContext();
  const t = detail.ticket;
  const c = detail.customer;
  const [f, setF] = useState({
    firstName: c?.firstName ?? "", lastName: c?.lastName ?? "", cardNumber: "", month: "", year: "", cardCode: "",
    address: t.street ?? "", address2: t.street2 ?? "", city: t.city ?? "", state: t.state ?? "", zip: t.zip ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  const brand = cardBrand(f.cardNumber);
  const numberProblem = cardNumberProblem(f.cardNumber, false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const problem = cardNumberProblem(f.cardNumber, true) ?? cardCodeProblem(f.cardCode, f.cardNumber, true);
    if (problem) { setError(problem); return; }
    setError("");
    setBusy(true);
    try {
      await loadAccept(cfg.script);
      const opaqueData = await tokenize(cfg, { cardNumber: f.cardNumber.replace(/\D/g, ""), month: f.month, year: f.year, cardCode: f.cardCode.trim() });
      setF((x) => ({ ...x, cardNumber: "", cardCode: "" })); // the number is not kept in the page
      const { cardNumber: _n, cardCode: _c, month: _m, year: _y, ...billTo } = f;
      if (await save("POST", "/card-save", { opaqueData, billTo })) onDone();
    } catch (err) {
      setError((err as Error).message);
    } finally { setBusy(false); }
  };
  return (
    <form className="mini-form card-form" onSubmit={(e) => void submit(e)} autoComplete="on">
      <h3 className="form-title">Payment Information</h3>
      <div className="two"><label>First Name<input required value={f.firstName} onChange={set("firstName")} autoComplete="cc-given-name" /></label>
        <label>Last Name<input required value={f.lastName} onChange={set("lastName")} autoComplete="cc-family-name" /></label></div>
      <div className="card-row">
        <label>Card Number
          <span className="card-input">
            <input required inputMode="numeric" value={f.cardNumber} autoComplete="cc-number" maxLength={brand === "Amex" ? 17 : 23}
              placeholder={brand === "Amex" ? "XXXX XXXXXX XXXXX" : "XXXX XXXX XXXX XXXX"} aria-invalid={!!numberProblem}
              onChange={(e) => setF({ ...f, cardNumber: formatCardNumber(e.target.value) })} />
            {brand && <span className="card-brand" aria-live="polite">{brand}</span>}
          </span>
          {numberProblem && <span className="field-warn">{numberProblem}</span>}
        </label>
        <label>Expiration Month<select required value={f.month} onChange={set("month")} autoComplete="cc-exp-month">
          <option value="">Select a Month</option>{MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}</select></label>
        <label>Expiration Year<select required value={f.year} onChange={set("year")} autoComplete="cc-exp-year">
          <option value="">Select a Year</option>{YEARS.map((y) => <option key={y} value={y}>{y}</option>)}</select></label>
        <label>Security Code<input inputMode="numeric" value={f.cardCode} autoComplete="cc-csc" maxLength={codeLength(brand)}
          onChange={(e) => setF({ ...f, cardCode: e.target.value.replace(/\D/g, "").slice(0, codeLength(brand)) })}
          placeholder={`optional, ${codeLength(brand)} digits`} /></label>
      </div>
      <h3 className="form-title">Billing Address</h3>
      <label>Street Address<input required value={f.address} onChange={set("address")} autoComplete="billing address-line1" /></label>
      <label>Apartment, suite, etc. (optional)<input value={f.address2} onChange={set("address2")} autoComplete="billing address-line2" /></label>
      <label>City<input required value={f.city} onChange={set("city")} autoComplete="billing address-level2" /></label>
      <div className="two"><label>State<input required value={f.state} onChange={set("state")} autoComplete="billing address-level1" /></label>
        <label>Zip code<input required value={f.zip} onChange={set("zip")} autoComplete="billing postal-code" /></label></div>
      {error && <p className="warn">{error}</p>}
      <p className="hint">Your card is saved securely with our payment processor.
        {detail.paymentEnvironment === "sandbox" && " Sandbox: use 4111 1111 1111 1111 and any future date."}</p>
      <div className="btn-row center">
        <button type="submit" className="btn primary" disabled={busy || saving}>{busy || saving ? "Saving…" : "Create"}</button>
        {onCancel && <button type="button" className="btn outline" onClick={onCancel}>Cancel</button>}
      </div>
    </form>
  );
}

/** The ticket's card: show it, add one in the portal's card form, remove it. */
export function CardDialog({ onClose }: { onClose: () => void }) {
  const { config, detail, save, saving } = useTicketContext();
  const auth = useAuth();
  const [error, setError] = useState("");
  const [opened, setOpened] = useState(false);
  const [form, setForm] = useState<{ accept: AcceptConfig | null } | null>(null);
  const [adding, setAdding] = useState(!detail.card);
  const c = detail.card;
  const no = detail.ticket.ticketNo;
  const token = auth.user?.id_token ?? "";
  useEffect(() => {
    apiSend<{ accept: AcceptConfig | null }>(config, token, "POST", `/tickets/${no}/card-form`)
      .then(setForm).catch((e: Error) => { setError(e.message); setForm({ accept: null }); });
  }, [config, token, no]);
  const startHosted = () => {
    setError("");
    openHostedCardPage(() => apiSend<{ url: string; token: string }>(config, token, "POST", `/tickets/${no}/card-session`))
      .then(() => setOpened(true)).catch((e: Error) => setError(e.message));
  };
  return (
    <Modal title="Manage Payment Profile" onClose={onClose}>
      <div className="mini-form">
        {c ? (
          <div className="card-info">
            <strong>{cardLabel(c)}</strong>{c.expiration && c.expiration !== "XXXX" && <> · expires {c.expiration}</>}
            {c.billing && <div className="hint">{[[c.billing.firstName, c.billing.lastName].filter(Boolean).join(" "), c.billing.address, [c.billing.city, c.billing.state, c.billing.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</div>}
            {/* Compared with this deployment's Authorize.Net: after go-live, live cards are the normal ones (owner, 2026-11-04). */}
            {c.environment !== detail.paymentEnvironment
              ? <p className="warn">{c.environment === "production"
                ? "Saved in the old system (live Authorize.Net). Until go-live it can't be charged or removed here."
                : "Saved in the Authorize.Net sandbox (testing). It can't be charged; add the customer's card."}</p>
              : c.environment === "sandbox" ? <p className="hint">Authorize.Net sandbox (test card).</p> : null}
          </div>
        ) : !adding && <p className="muted">No card on file for this ticket.</p>}
        {error && <p className="warn">{error}</p>}
        {!form ? <p className="muted">Loading…</p> : form.accept ? (
          adding ? <CardForm cfg={form.accept} onDone={() => setAdding(false)} onCancel={c ? () => setAdding(false) : undefined} /> : null
        ) : (
          <p className="hint">The card form needs Authorize.Net's Public Client Key, which hasn't been entered yet. Until then, cards are
            added in Authorize.Net's own page (opens in a new tab).</p>
        )}
        {!(form?.accept && adding) && (
          <div className="btn-row wrap">
            {form?.accept
              ? <button type="button" className="btn primary" disabled={saving} onClick={() => setAdding(true)}>{c ? "Replace with a new card" : "Add a card"}</button>
              : form && <button type="button" className="btn primary" disabled={saving} onClick={startHosted}>{c ? "Change or add a card" : "Add a card"}</button>}
            {opened && <button type="button" className="btn outline" disabled={saving} onClick={() => void save("POST", "/card-sync").then((d) => d && setOpened(false))}>I'm done: update from Authorize.Net</button>}
            {c && c.environment === detail.paymentEnvironment && (
              <button type="button" className="btn outline danger" disabled={saving}
                onClick={() => { void confirmBox(`Remove the ${cardLabel(c)} from Authorize.Net? It can't be charged afterwards.`).then((ok) => { if (ok) void save("POST", "/card-delete"); }); }}>Remove card</button>
            )}
            <button type="button" className="btn outline" onClick={onClose}>Close</button>
          </div>
        )}
      </div>
    </Modal>
  );
}

/** Back from Authorize.Net's form (the page reopens with ?card=done): read the cards back once. */
export function useCardReturn() {
  const { save } = useTicketContext();
  useEffect(() => {
    const u = new URL(window.location.href);
    if (u.searchParams.get("card") !== "done") return;
    u.searchParams.delete("card");
    window.history.replaceState(null, "", u.toString());
    void save("POST", "/card-sync");
  }, [save]);
}

/** Refund a card payment, full or partial (owner: customer emailed, status unchanged). */
export function RefundDialog({ onClose }: { onClose: () => void }) {
  const { detail, save, saving } = useTicketContext();
  const charges = detail.payments.filter((p) => p.refundable && Number(p.refundable) > 0);
  const [paymentId, setPaymentId] = useState(charges[0]?.id ?? "");
  const chosen = charges.find((p) => p.id === paymentId);
  const [amount, setAmount] = useState(chosen?.refundable ?? "");
  return (
    <Modal title="Refund" onClose={onClose}>
      {charges.length === 0 ? <p className="muted">No card payments on this ticket to refund.</p> : (
        <form className="mini-form" onSubmit={async (e) => {
          e.preventDefault();
          if (!(await confirmBox(`Refund ${money(amount)} to the customer's card? This can't be undone.`, { title: "Refund", ok: "Refund", danger: true }))) return;
          if (await save("POST", "/refunds", { paymentId, amount: Number(amount) })) onClose();
        }}>
          <fieldset className="radios"><legend className="lbl">Which payment?</legend>
            {charges.map((p) => (
              <label key={p.id}><input type="radio" name="charge" checked={p.id === paymentId}
                onChange={() => { setPaymentId(p.id); setAmount(p.refundable ?? ""); }} />
                {date(p.paidAt ?? p.createdAt)} · {money(p.amount)}{Number(p.refundable) < Number(p.amount) ? ` (${money(p.refundable)} left)` : ""}</label>
            ))}
          </fieldset>
          <label>Amount to refund ($)<input type="number" min="0.01" step="0.01" max={chosen?.refundable ?? undefined} required value={amount}
            onChange={(e) => setAmount(e.target.value)} autoFocus /></label>
          <p className="hint">The customer is emailed; the status does not change. A charge from today that hasn't settled yet can only be refunded in full (it is cancelled).</p>
          <div className="btn-row"><button type="submit" className="btn primary" disabled={saving}>Refund</button>
            <button type="button" className="btn outline" onClick={onClose}>Cancel</button></div>
        </form>
      )}
    </Modal>
  );
}
